"use strict";
/**
 * Andrich Rating-Scale Model (RSM) — core math.
 *
 * Polytomous Rasch model with item discrimination fixed at 1.0. Each item has
 * a location parameter δ_i; category thresholds τ_1..τ_K are shared across
 * all items in the rating scale.
 *
 * Reference: Andrich, D. (1978). A rating formulation for ordered response
 * categories. Psychometrika, 43(4), 561–573.
 *
 * The category-probability formula is
 *
 *   P(X_i = k | θ) = exp(Σ_{j=1}^{k}(θ − δ_i − τ_j)) / Σ_{m=0}^{K} exp(Σ_{j=1}^{m}(θ − δ_i − τ_j))
 *
 * with Σ_{j=1}^{0}(·) = 0 by convention (so the numerator for k = 0 is 1).
 *
 * Implementation note — the exponent for category k simplifies to
 *   e_k = k·(θ − δ) − T_k
 * where T_k = Σ_{j=1}^{k} τ_j is the running threshold sum (T_0 = 0).
 *
 * To avoid overflow at extreme θ we apply the log-sum-exp shift:
 *   P_k = exp(e_k − M) / Σ_m exp(e_m − M), where M = max_m e_m.
 *
 * For Rasch-family polytomous models, item Fisher information equals the
 * variance of the response distribution at θ:
 *   I_i(θ) = E[X² | θ] − (E[X | θ])²
 * This is a standard analytical result and is exactly the test we use.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.rsmMLE = exports.rsmTotalInformation = exports.rsmScore = exports.rsmNegativeLogLikelihood = exports.rsmLogLikelihood = exports.rsmFisherInformation = exports.rsmExpectedScore = exports.rsmCategoryProbabilities = void 0;
/**
 * Compute the running threshold cumulative sums T_k = Σ_{j=1}^{k} τ_j for
 * k = 0..K. Returns an array of length K+1 with T_0 = 0.
 *
 * @param {number[]} tau
 * @returns {number[]}
 */
function cumulativeThresholds(tau) {
    const T = new Array(tau.length + 1);
    T[0] = 0;
    for (let k = 1; k <= tau.length; k += 1) {
        T[k] = T[k - 1] + tau[k - 1];
    }
    return T;
}
/**
 * Andrich RSM category probabilities for a single item at ability θ.
 *
 * @param {number} theta
 * @param {number} delta              item location δ_i
 * @param {number[]} tau              shared category thresholds τ_1..τ_K (length K)
 * @returns {number[]}                length K+1; index k is P(X = k | θ)
 */
function rsmCategoryProbabilities(theta, delta, tau) {
    const T = cumulativeThresholds(tau);
    const K = tau.length;
    const exponents = new Array(K + 1);
    let maxE = -Infinity;
    for (let k = 0; k <= K; k += 1) {
        const e = k * (theta - delta) - T[k];
        exponents[k] = e;
        if (e > maxE)
            maxE = e;
    }
    let denom = 0;
    const numerators = new Array(K + 1);
    for (let k = 0; k <= K; k += 1) {
        const v = Math.exp(exponents[k] - maxE);
        numerators[k] = v;
        denom += v;
    }
    const probs = new Array(K + 1);
    for (let k = 0; k <= K; k += 1) {
        probs[k] = numerators[k] / denom;
    }
    return probs;
}
exports.rsmCategoryProbabilities = rsmCategoryProbabilities;
/**
 * Expected score E[X | θ] = Σ k · P_k(θ).
 *
 * @param {number} theta
 * @param {number} delta
 * @param {number[]} tau
 * @returns {number}
 */
function rsmExpectedScore(theta, delta, tau) {
    const probs = rsmCategoryProbabilities(theta, delta, tau);
    let mean = 0;
    for (let k = 0; k < probs.length; k += 1) {
        mean += k * probs[k];
    }
    return mean;
}
exports.rsmExpectedScore = rsmExpectedScore;
/**
 * Fisher information for a single item under RSM at ability θ.
 *
 * For Rasch-family polytomous models this equals the variance of the response
 * distribution at θ:
 *   I_i(θ) = E[X² | θ] − (E[X | θ])²
 * which is also the standard analytical form derived from (∂lnP_k/∂θ)² weighted
 * by P_k. (Andrich 1978; Embretson & Reise 2000.)
 *
 * @param {number} theta
 * @param {number} delta
 * @param {number[]} tau
 * @returns {number}
 */
function rsmFisherInformation(theta, delta, tau) {
    const probs = rsmCategoryProbabilities(theta, delta, tau);
    let e1 = 0;
    let e2 = 0;
    for (let k = 0; k < probs.length; k += 1) {
        e1 += k * probs[k];
        e2 += k * k * probs[k];
    }
    // Floating-point negatives can creep in at extreme θ via 0 − 0; clamp.
    const variance = e2 - e1 * e1;
    return variance < 0 ? 0 : variance;
}
exports.rsmFisherInformation = rsmFisherInformation;
/**
 * @typedef {object} RSMObservation
 * @property {number} delta           item location for the observation
 * @property {number[]} tau           shared τ vector for the item's section
 * @property {number} k               the observed category (the IRT-input score,
 *                                    0..K, not the raw response value)
 */
/**
 * Log-likelihood at θ of a set of observed responses under RSM.
 *
 * Returns Σ_i ln P(X_i = k_i | θ). Returns 0 for an empty observation list.
 *
 * @param {number} theta
 * @param {RSMObservation[]} observations
 * @returns {number}
 */
function rsmLogLikelihood(theta, observations) {
    if (!observations || observations.length === 0)
        return 0;
    let ll = 0;
    for (let i = 0; i < observations.length; i += 1) {
        const { delta, tau, k } = observations[i];
        const probs = rsmCategoryProbabilities(theta, delta, tau);
        // Defensive: clamp at a tiny positive to avoid log(0). Should not occur
        // under the log-sum-exp implementation, but the guard is cheap.
        const p = probs[k];
        ll += Math.log(p > 0 ? p : Number.MIN_VALUE);
    }
    return ll;
}
exports.rsmLogLikelihood = rsmLogLikelihood;
/**
 * Convenience: negative log-likelihood, for use with minimisers.
 *
 * @param {number} theta
 * @param {RSMObservation[]} observations
 * @returns {number}
 */
function rsmNegativeLogLikelihood(theta, observations) {
    return -rsmLogLikelihood(theta, observations);
}
exports.rsmNegativeLogLikelihood = rsmNegativeLogLikelihood;
/**
 * Score function (first derivative of log-likelihood) for RSM at θ.
 *
 *   ℓ'(θ) = Σ_i (k_i − E[X_i | θ])
 *
 * Observed minus expected, summed across items. Standard Rasch-family result.
 *
 * @param {number} theta
 * @param {RSMObservation[]} observations
 * @returns {number}
 */
function rsmScore(theta, observations) {
    let s = 0;
    for (let i = 0; i < observations.length; i += 1) {
        const { delta, tau, k } = observations[i];
        s += k - rsmExpectedScore(theta, delta, tau);
    }
    return s;
}
exports.rsmScore = rsmScore;
/**
 * Total Fisher information across observations at θ (sum of per-item variances).
 * This is the negative Hessian of the log-likelihood, used in Newton iteration.
 *
 * @param {number} theta
 * @param {RSMObservation[]} observations
 * @returns {number}
 */
function rsmTotalInformation(theta, observations) {
    let info = 0;
    for (let i = 0; i < observations.length; i += 1) {
        const { delta, tau } = observations[i];
        info += rsmFisherInformation(theta, delta, tau);
    }
    return info;
}
exports.rsmTotalInformation = rsmTotalInformation;
/**
 * Newton-Raphson MLE for θ under RSM.
 *
 * Uses the analytical score and Hessian (= −Σ I_i). Bounded to [-5, 5] to
 * match the legacy 2PL ML behaviour for QA on extreme response patterns
 * (saturation at the bound is intentional under MLE — see Phase 1 §2.4 of
 * RSM_MIGRATION_PLAN.md).
 *
 * @param {RSMObservation[]} observations
 * @param {object} [options]
 * @param {number} [options.theta0]      starting value, default 0
 * @param {number} [options.tolerance]   |Δθ| convergence threshold, default 1e-6
 * @param {number} [options.maxIter]     max iterations, default 25
 * @param {number} [options.minTheta]    lower bound, default -5
 * @param {number} [options.maxTheta]    upper bound, default +5
 * @returns {number}
 */
function rsmMLE(observations, options = {}) {
    if (!observations || observations.length === 0)
        return 0;
    const { theta0 = 0, tolerance = 1e-6, maxIter = 25, minTheta = -5, maxTheta = 5, } = options;
    let theta = theta0;
    for (let iter = 0; iter < maxIter; iter += 1) {
        const score = rsmScore(theta, observations);
        const info = rsmTotalInformation(theta, observations);
        // If information collapses (extreme θ), bail to the nearest bound based
        // on score direction. Same saturation behaviour as 2PL ML at extremes.
        if (info < 1e-12) {
            if (score > 0)
                return maxTheta;
            if (score < 0)
                return minTheta;
            return theta;
        }
        let step = score / info;
        // Cap step to avoid overshoot.
        if (step > 1.5)
            step = 1.5;
        if (step < -1.5)
            step = -1.5;
        let next = theta + step;
        if (next < minTheta)
            next = minTheta;
        if (next > maxTheta)
            next = maxTheta;
        if (Math.abs(next - theta) < tolerance)
            return next;
        theta = next;
    }
    return theta;
}
exports.rsmMLE = rsmMLE;
//# sourceMappingURL=ratingScale.js.map